import React, { useState } from 'react';

const initial = {
  name: '',
  email: '',
  phone: '',
  date: '',
  time: '',
  company: '',
  projectType: 'Website',
  budget: 'Under $5k',
  message: ''
};

const ConnectSection = () => {
  const [active, setActive] = useState('call');
  const [form, setForm] = useState(initial);
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [sending, setSending] = useState(false);
  const nameInputRef = React.useRef(null);
  const ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_KEY || '';

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  React.useEffect(() => {
    const openFromHash = () => {
      if (window.location.hash === '#connect') {
        setOpen(true);
        setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 100);
      }
    };
    openFromHash();
    const handleOpen = () => {
      setOpen(true);
      setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 100);
    };
    window.addEventListener('hashchange', openFromHash);
    window.addEventListener('open-connect', handleOpen);
    return () => {
      window.removeEventListener('hashchange', openFromHash);
      window.removeEventListener('open-connect', handleOpen);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setNote('');
    setResult('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) || !form.name.trim()) {
      setNote('Please provide your name and a valid email.');
      return;
    }
    if (!ACCESS_KEY) {
      setResult('Contact form not configured. Add REACT_APP_WEB3FORMS_KEY.');
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('access_key', ACCESS_KEY);
      fd.append('name', form.name);
      fd.append('email', form.email);
      const compiled = active === 'call'
        ? `Type: Schedule Call\nPhone: ${form.phone}\nPreferred: ${form.date || '-'} ${form.time || ''}\nMessage: ${form.message || '-'}`
        : `Type: Get Quote\nCompany: ${form.company || '-'}\nProject: ${form.projectType}\nBudget: ${form.budget}\nBrief: ${form.message || '-'}`;
      fd.append('message', compiled);
      fd.append('subject', active === 'call' ? 'Schedule Free Call' : 'Get a Quote');
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setResult('Form Submitted Successfully');
        setForm(initial);
      } else {
        setResult(data.message || 'Submission failed');
      }
    } catch (err) {
      setResult('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="connect" className="relative z-10">
      <div className="max-w-5xl mx-auto">
        <div className={`flex justify-center mb-6 transition-all duration-500 ${open ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 max-h-0 overflow-hidden pointer-events-none'}`}>
          <div className="inline-flex rounded-lg overflow-hidden border border-gray-700/60 bg-gray-900/40">
            <button
              type="button"
              onClick={() => { setOpen(true); setActive('call'); setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 50); }}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${active === 'call' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:text-white'}`}
            >
              Schedule Free Call
            </button>
            <button
              type="button"
              onClick={() => { setOpen(true); setActive('quote'); setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 50); }}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${active === 'quote' ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:text-white'}`}
            >
              Get a Quote
            </button>
          </div>
        </div>

        <div className={`grid md:grid-cols-5 gap-6 transition-all duration-500 ${open ? 'opacity-100 translate-y-0 py-12' : 'opacity-0 -translate-y-2 max-h-0 overflow-hidden py-0 pointer-events-none'}`}>
          <div className="md:col-span-3 bg-gray-800/40 border border-gray-700/50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-white mb-4">{active === 'call' ? 'Schedule Your Free Call' : 'Request a Project Quote'}</h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1" htmlFor="name">Name</label>
                  <input ref={nameInputRef} id="name" name="name" value={form.name} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1" htmlFor="email">Email</label>
                  <input id="email" type="email" name="email" value={form.email} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" required />
                </div>
              </div>

              {active === 'call' ? (
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" value={form.phone} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="date">Preferred Date</label>
                    <input id="date" type="date" name="date" value={form.date} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="time">Preferred Time</label>
                    <input id="time" type="time" name="time" value={form.time} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="md:col-span-1">
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="company">Company</label>
                    <input id="company" name="company" value={form.company} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="projectType">Project Type</label>
                    <select id="projectType" name="projectType" value={form.projectType} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none">
                      <option>Website</option>
                      <option>Web App</option>
                      <option>Mobile App</option>
                      <option>Automation/AI</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-1" htmlFor="budget">Budget</label>
                    <select id="budget" name="budget" value={form.budget} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none">
                      <option>Under $5k</option>
                      <option>$5k - $15k</option>
                      <option>$15k - $50k</option>
                      <option>$50k+</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-300 mb-1" htmlFor="message">{active === 'call' ? 'Notes' : 'Project Brief'}</label>
                <textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} className="w-full px-3 py-2 rounded-lg bg-gray-900/60 border border-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>

              <button type="submit" disabled={sending} className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg font-semibold transition-colors">
                {sending ? 'Sending...' : (active === 'call' ? 'Schedule My Free Call' : 'Get My Quote')}
              </button>
              {note && <p className="text-center text-sm text-gray-300">{note}</p>}
              {result && <p className="text-center text-sm text-gray-300">{result}</p>}
            </form>
          </div>

          <aside className="md:col-span-2 p-6 rounded-xl bg-gray-800/30 border border-gray-700/50">
            <h4 className="text-white font-semibold">How it works</h4>
            <ul className="mt-3 list-disc list-inside text-gray-300 space-y-1 text-sm">
              <li>Submit the form with your details.</li>
              <li>We'll email you within 1 business day.</li>
              <li>For calls, we confirm a time and send a calendar invite.</li>
              <li>For quotes, expect timelines, scope, and budget ranges.</li>
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default ConnectSection;

